import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { Text } from '@fiverr-private/typography';
import { regulationTranslation } from '@fiverr-private/price_regulation_translations';
import { useCalculatePriceBreakdown, BUYABLE_TYPE } from '@fiverr-private/price_upfront_sdk';
import { priceMarkupFormatter } from '../../../GigCardListings/ListingsTransformer/priceTransformer';
import {
    PRICE_LABEL_TYPES,
    PRICE_DEFAULT_LABELS,
} from '../../../GigCardListings/ListingsTransformer/priceTransformer/constants';
import { useGigListingsContext } from '../../../GigCardListings/context/GigListingsContext';
import { trackGigForClickType, createClickedElement } from '../../utils';
import isDefualtCurrency from '../../../GigCardListings/utils/defualtCurrency';
import ContextualLink from '../ContextualLink';
import { OPEN_GIG_SAME_TAB_TARGET, ELEMENTS_NAME, ELEMENTS_TYPE } from '../constants';
import TooltipWrapper from '../../../shared/components/TooltipWrapper';
import { PriceUpfrontTooltip } from '../../../shared/components/PriceUpfrontTooltip';
import './price.scss';

const Price = (props, context) => {
    const {
        url,
        price = 0,
        originalPrice,
        urlTarget,
        priceLabelType,
        showFrom,
        priceDisplayStatus,
        showHourlyPrice = false,
        hourlyRate,
    } = props;
    const { currencyObj } = useGigListingsContext();
    const { calculatePriceBreakdown } = useCalculatePriceBreakdown();
    const { name } = currencyObj;
    const defaultLabel = showFrom ? PRICE_DEFAULT_LABELS.FROM : PRICE_DEFAULT_LABELS.STARTING_AT;
    const priceLabel = priceLabelType === PRICE_LABEL_TYPES.DEFAULT ? defaultLabel : priceLabelType;
    const { trackEvent } = context;

    const regulationTranslationText = regulationTranslation(priceDisplayStatus);

    const displayPrice = useMemo(() => {
        if (showHourlyPrice) {
            return hourlyRate ?? '';
        }
        if (!originalPrice) {
            return price;
        }
        const priceBreakdown = calculatePriceBreakdown({
            netPrice: originalPrice,
            buyableType: BUYABLE_TYPE.GIG,
        });
        if (priceBreakdown) {
            return Math.ceil(priceBreakdown.gross_price * currencyObj.rate);
        }
        return price;
    }, [originalPrice, price, calculatePriceBreakdown, currencyObj.rate, showHourlyPrice, hourlyRate]);

    if (showHourlyPrice && !hourlyRate) {
        return null;
    }

    const clickedPriceElement = clickedElement(displayPrice);
    const showTooltip = !showHourlyPrice && displayPrice !== price;

    return (
        <TooltipWrapper content={<PriceUpfrontTooltip />} shouldRenderTooltip={showTooltip} position="bottom">
            <ContextualLink
                href={url}
                target={urlTarget}
                className={classNames('price', { 'line-break': !isDefualtCurrency(name) })}
                onMouseDown={(e) => trackGigForClickType(e.button, trackEvent, clickedPriceElement)}
            >
                {!showHourlyPrice && priceLabel && (
                    <small className="text-body-3">
                        <I18n k={`gig_listings.gig_cards.price.${priceLabel}`} />
                    </small>
                )}
                <span
                    dangerouslySetInnerHTML={{ __html: priceMarkupFormatter({ currencyObj, price: displayPrice }) }}
                />
                {showHourlyPrice && (
                    <>
                        <I18n k="gig_listings.gig_cards.price.hourly.suffix" />
                        <Text paddingLeft="1.5" fontWeight="normal" color="bodySecondary" as="span">
                            <I18n k="gig_listings.gig_cards.price.hourly.taxes_and_fees" />
                        </Text>
                    </>
                )}
                {!showHourlyPrice && regulationTranslationText && (
                    <Text paddingLeft="1.5" fontWeight="normal" color="bodySecondary" as="span">
                        {regulationTranslationText}
                    </Text>
                )}
            </ContextualLink>
        </TooltipWrapper>
    );
};

const PriceType = PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]);

Price.propTypes = {
    url: PropTypes.string.isRequired,
    price: PriceType,
    priceLabelType: PropTypes.string,
    urlTarget: PropTypes.string,
    originalPrice: PriceType,
    showFrom: PropTypes.bool,
    priceDisplayStatus: PropTypes.oneOf(['fees', 'taxes', 'both', 'none']),
    showHourlyPrice: PropTypes.bool,
    hourlyRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Price.defaultProps = {
    urlTarget: OPEN_GIG_SAME_TAB_TARGET,
    priceDisplayStatus: 'none',
};

Price.contextTypes = {
    trackEvent: PropTypes.func.isRequired,
};

export const clickedElement = (price) => createClickedElement(ELEMENTS_NAME.GIG_PRICE, ELEMENTS_TYPE.LINK, price);

export default Price;
